import { useState } from 'react';
import { LogOut, User, Menu, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useSidebar } from '../../contexts/SidebarContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import AlertNotificationBell from '../alerts/AlertNotificationBell';
import MobileNav from './MobileNav';

export default function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { toggleSidebar } = useSidebar();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMenuClick = () => {
    // On mobile (< md), open the mobile nav drawer
    // On desktop, toggle the sidebar collapse
    if (window.innerWidth < 768) {
      setMobileNavOpen(true);
    } else {
      toggleSidebar();
    }
  };

  return (
    <>
      <MobileNav isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      
      <header className="sticky top-0 z-50 w-full border-b bg-white dark:bg-gray-900 dark:border-gray-800 shadow-sm">
        <div className="flex h-14 sm:h-16 items-center px-4 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Toggle Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleMenuClick}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800"
            >
              <Menu size={20} />
            </Button>
            
            <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-blue-700 text-white font-bold text-sm sm:text-lg shadow-sm">
              AF
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">AutoFlo</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Commercial Planning Workspace</p>
            </div>
          </div>

          <div className="ml-6 hidden w-full max-w-md items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-500 lg:flex">
            <Search className="mr-2 h-4 w-4 text-slate-400" />
            Search programs, parts, RFQs, claims...
          </div>

          <div className="ml-auto flex items-center gap-2 sm:gap-4">
          {/* System Alerts Bell - Single unified notification center */}
          <AlertNotificationBell />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <User size={18} />
                </div>
                <div className="text-left hidden md:block">
                  <div className="text-sm font-medium">
                    {user?.first_name || user?.email?.split('@')[0] || 'User'}
                  </div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="px-2 py-2 text-sm">
                <div className="font-medium">{user?.email}</div>
                <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                {user?.company_name && (
                  <div className="text-xs text-gray-500 mt-1">{user.company_name}</div>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
    </>
  );
}
